/**
 * SPEC-COOKIE — cookie + ID helpers for consent. Framework-free (no React,
 * no Supabase) so COOKIE-3 can drop this into the portal verbatim.
 *
 * The parent-domain cookie (`.quanitra.com`) is the UX source of truth:
 * the banner never reads the database on every page load. The Supabase
 * table is the durable legal record; this module just handles the cookie
 * round-trip and the anonymous browser id.
 *
 * All access wrapped in try/catch — private mode + jsdom + some
 * corporate-policy browsers can throw on `document.cookie`. A failure
 * must NOT block the visitor; we degrade to a fresh consentId in
 * memory and treat any read failure as "no decision yet".
 */

import {
  CONSENT_COOKIE_DOMAIN,
  CONSENT_COOKIE_MAX_AGE_DAYS,
  CONSENT_COOKIE_NAME,
  POLICY_VERSION,
  type ConsentDecision,
} from "./consent-config";

// ─── cookie primitives ──────────────────────────────────────────────────

function readRawCookie(name: string): string | null {
  try {
    if (typeof document === "undefined") return null;
    const raw = document.cookie || "";
    const target = `${name}=`;
    for (const part of raw.split(";")) {
      const trimmed = part.trim();
      if (trimmed.startsWith(target)) {
        return decodeURIComponent(trimmed.slice(target.length));
      }
    }
    return null;
  } catch {
    return null;
  }
}

function writeRawCookie(name: string, value: string): void {
  try {
    if (typeof document === "undefined") return;
    const maxAge = CONSENT_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
    // `Secure` is fine — production is https. On localhost the cookie
    // simply won't set the domain attribute; that is acceptable for dev.
    // `Domain=.quanitra.com` shares the cookie across every subdomain.
    const parts = [
      `${name}=${encodeURIComponent(value)}`,
      `Domain=${CONSENT_COOKIE_DOMAIN}`,
      "Path=/",
      `Max-Age=${maxAge}`,
      "SameSite=Lax",
      "Secure",
    ];
    document.cookie = parts.join("; ");
  } catch {
    // swallow — never block the visitor on a cookie write failure
  }
}

// ─── ConsentDecision codec ──────────────────────────────────────────────

/**
 * Parse the `quanitra_consent` cookie. Returns null when the cookie is
 * absent, when JSON parsing fails, or when the shape doesn't match.
 * Defensive: a corrupted cookie should NOT crash the app — treat it as
 * "no decision".
 */
export function readConsentCookie(): ConsentDecision | null {
  const raw = readRawCookie(CONSENT_COOKIE_NAME);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const o = parsed as Record<string, unknown>;
    if (
      typeof o.consentId !== "string" ||
      typeof o.policyVersion !== "string" ||
      typeof o.decidedAt !== "string" ||
      typeof o.action !== "string"
    ) {
      return null;
    }
    return {
      necessary: true,
      analytics: o.analytics === true,
      marketing: o.marketing === true,
      action: o.action as ConsentDecision["action"],
      policyVersion: o.policyVersion,
      consentId: o.consentId,
      decidedAt: o.decidedAt,
    };
  } catch {
    return null;
  }
}

/** Serialize a decision and write the parent-domain cookie. */
export function writeConsentCookie(decision: ConsentDecision): void {
  writeRawCookie(CONSENT_COOKIE_NAME, JSON.stringify(decision));
}

/**
 * Return the existing decision's consentId, or mint a fresh UUID. We
 * never re-use a stored id when the cookie can't be read — the worst
 * case is a slightly fragmented audit trail, never a wrong attribution.
 */
export function getOrCreateConsentId(): string {
  const existing = readConsentCookie();
  if (existing && existing.consentId) return existing.consentId;
  // crypto.randomUUID is standard on every browser this app targets and
  // on jsdom (node ≥ 19). Fall back defensively just in case.
  try {
    return crypto.randomUUID();
  } catch {
    return `c-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * The decision is current iff it exists AND the stored policyVersion
 * matches the live POLICY_VERSION. Bumping the constant in
 * `consent-config.ts` invalidates every stored decision → re-prompt.
 */
export function isDecisionCurrent(d: ConsentDecision | null): boolean {
  if (!d) return false;
  return d.policyVersion === POLICY_VERSION;
}
